import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../lib/supabaseClient';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import { setBackendToken, setProfile } from '../../redux/slices/authSlice';
import { useExchangeTokenMutation, useLazyGetMeQuery } from '../../redux/api/authApi';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { mapSupabaseErrorMessage } from './authErrors';
import AnimatedBackground from '../../components/AnimatedBackground';
import Logo from '../../components/Logo';
import { Eye, EyeOff } from 'lucide-react';
import { useLazyGetDefaultPortfolioQuery, useLazyListPortfolioSitesQuery } from '../../redux/api/portfolioApi';
import { organizationApi } from '../../redux/api/organizationApi';
import { setSelectedPortfolioId, setPortfolios } from '../../redux/slices/portfolioSlice';
import { setSitesForPortfolio } from '../../redux/slices/siteSlice';

const loginSchema = z.object({
  email: z.email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Please enter your password'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [exchangeToken, { isLoading: isExchanging }] = useExchangeTokenMutation();
  const [triggerGetMe] = useLazyGetMeQuery();
  const [triggerGetDefaultPortfolio] = useLazyGetDefaultPortfolioQuery();
  const [triggerListPortfolioSites] = useLazyListPortfolioSitesQuery();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    const trimmedEmail = data.email.trim();
    const trimmedPassword = data.password.trim();

    try {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password: trimmedPassword,
      });
      if (signInError) throw signInError;

      const supabaseToken = signInData.session?.access_token;
      if (!supabaseToken) throw new Error('No Supabase session token');

      const invitationToken = localStorage.getItem('invitation_token');

      const resp = await exchangeToken({
        supabase_token: supabaseToken,
        device_info: {},
        ...(invitationToken ? { invitation_token: invitationToken } : {}),
      }).unwrap();

      if (invitationToken) {
        localStorage.removeItem('invitation_token');
      }

      dispatch(
        setBackendToken({
          accessToken: resp.access_token,
          tokenType: resp.token_type,
          expiresIn: resp.expires_in,
        })
      );

      const me = await triggerGetMe().unwrap();
      dispatch(
        setProfile({
          id: me.id ?? null,
          email: me.email ?? null,
          firstName: me.first_name ?? null,
          lastName: me.last_name ?? null,
          phone: me.phone ?? null,
          role: me.role ?? null,
          tenant: me.tenant ?? null, // Legacy field for backward compatibility
          active_tenant: (me as any).active_tenant ?? null,
          tenants: (me as any).tenants ?? null,
          activeSite: me.active_site ?? null,
          sessionId: me.session_id ?? null,
          permissions: me.permissions ?? null,
          isActive: typeof me.is_active === 'boolean' ? me.is_active : null,
          is_platform_admin: typeof (me as any).is_platform_admin === 'boolean' ? (me as any).is_platform_admin : null,
        })
      );

      // Extract tenant_id from profile
      const rawTenant = me.tenant as any;
      const tenantId = rawTenant?.id || rawTenant?.tenant_id || (me as any)?.tenant_id || null;

      // If user has a tenant, fetch organization first to establish context
      // This ensures the backend knows about the organization before portfolio requests
      if (tenantId) {
        try {
          // Fetch organization to establish tenant context in backend
          const orgResult = await dispatch(organizationApi.endpoints.getOrganization.initiate(tenantId));
          if (orgResult.data) {
          }
        } catch (orgErr) {
          console.error('Failed to fetch organization on login:', orgErr);
          // Continue even if organization fetch fails - might not be critical
        }
      }

      // Initialize portfolio and sites on login
      try {
        // Only fetch portfolio if user has a tenant
        if (!tenantId) {
          console.warn('No tenant_id found, skipping portfolio initialization');
        } else {
          // Get default portfolio
          const portfolio = await triggerGetDefaultPortfolio().unwrap();
        if (portfolio) {
          // Store portfolio in Redux
          dispatch(setPortfolios([portfolio]));
          dispatch(setSelectedPortfolioId(portfolio.id));

          // Fetch sites for the portfolio - await to ensure it completes before navigation
          try {
            const sitesResponse = await triggerListPortfolioSites({
              portfolio_id: portfolio.id,
            }).unwrap();
            const sites = Array.isArray(sitesResponse)
              ? sitesResponse
              : (sitesResponse as any)?.items || [];
            if (sites.length > 0) {
              dispatch(setSitesForPortfolio({ portfolioId: portfolio.id, sites }));
            }
          } catch (sitesErr) {
            console.error('Failed to fetch sites on login:', sitesErr);
            // Continue even if sites fetch fails
          }
        }
        }
      } catch (err) {
        console.error('Failed to initialize portfolio on login:', err);
        // Don't block login if portfolio fetch fails
      }

      navigate('/');
    } catch (err: any) {
      const msg = mapSupabaseErrorMessage(err?.message) || 'Login failed';
      toast.error(msg);
    }
  };

  return (
    <div className="eg-auth-page auth-page">
      <AnimatedBackground />
      <div className="relative z-10 w-full max-w-2xl flex flex-col items-center">
        <div className="mb-4 pb-8 w-full">
          <Logo />
        </div>
        <div className="eg-auth-card">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-light tracking-wide" style={{ color: 'var(--color-auth-text)' }}>Login</h2>
          </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <input
              type="email"
              placeholder="Email"
              {...register('email')}
              className={`eg-auth-input ${errors.email ? 'input-error' : ''}`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                {...register('password')}
                className={`eg-auth-input pr-10 ${errors.password ? 'input-error' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors focus:outline-none"
                style={{ color: 'var(--color-auth-eye-icon)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-auth-eye-icon-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-auth-eye-icon)'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-400">{errors.password.message}</p>
            )}
            <div className="text-right">
              <Link to="/forgot-password" className="inline-block py-1 text-sm underline hover:text-[#17c480] transition-colors focus:outline-none focus:ring-2 focus:ring-[#17c480] rounded" style={{ color: 'var(--color-auth-link)' }}>
                Forgot password?
              </Link>
            </div>
          </div>
          <button
            type="submit"
            disabled={isExchanging || isSubmitting}
            className="eg-auth-btn"
          >
            {isExchanging || isSubmitting ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
        <div className="mt-3 text-sm text-center" style={{ color: 'var(--color-auth-text-secondary)' }}>
          <span>
            Don't have an account?{' '}
            <Link to="/signup" className="inline-block py-1 underline hover:text-[#17c480] transition-colors focus:outline-none focus:ring-2 focus:ring-[#17c480] rounded" style={{ color: 'var(--color-auth-link)' }}>
              Sign up
            </Link>
          </span>
        </div>
        <div className="mt-4 text-xs text-center" style={{ color: 'var(--color-auth-text-muted)' }}>
          <p>Need help? Contact <a href="mailto:support@engramiq.com" className="underline hover:text-[#17c480] transition-colors">support@engramiq.com</a></p>
        </div>
        </div>
      </div>
      {/* <SupportChat /> */}
    </div>
  );
};

export default LoginPage;
